import React, { useEffect, useState } from 'react'
import { Check, Lock } from 'lucide-react'
import { Button, Modal, Notice, Spinner } from '../../cases/ui'
import { pluralise } from '../../cases/format'

// ======================================================================
// EDITING A ROLE PEOPLE ALREADY HOLD
// ======================================================================
//
// THE SAME SHAPE AND THE SAME WORDING AS THE CASE TYPE PROMPT, on
// purpose. An administrator meets one of these when they change a
// procedure and the other when they change a role, and the question is
// identical in structure: something already running was built on the
// old definition, and both answers are ordinary. Two dialogs asking the
// same question in different words would make somebody read the second
// as a different kind of decision.
//
// WHAT DIFFERS IS WHAT CANNOT MOVE, and only that. A case cannot follow
// a case type edit when the stage it sits on has gone. A GRANT cannot
// follow a role edit when the new powers would put its holder on both
// sides of a separation — the edit adds the power to decide, and that
// person already investigates the same case. Blocked grants are named
// by case reference AND by person, because a count of two is not
// something anybody can act on.

export default function RoleMigrationPrompt({
  open,
  onClose,
  roleLabel,
  nextVersionNo,
  preview,
  previewError,
  busy,
  onConfirm,
}) {
  const [choice, setChoice] = useState(null)

  // Reset every time it opens: a remembered answer on a question this
  // consequential is a defect, not a convenience.
  useEffect(() => {
    if (open) setChoice(null)
  }, [open])

  const moved = preview?.migrated ?? 0
  const blocked = preview?.blocked || []
  const closed = preview?.closed_not_migrated ?? 0
  const running = moved + blocked.length

  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onClose}
      width="max-w-2xl"
      title="Apply these powers to people who already hold the role?"
      subtitle={
        roleLabel
          ? `Saving ${roleLabel} as version ${nextVersionNo}. Anybody given the role from now on gets the new powers.`
          : undefined
      }
    >
      {previewError ? (
        <Notice tone="amber" title="The change could not be checked">
          {previewError.message
            || 'The existing grants could not be checked against these powers, so nothing has been saved. Try again.'}
        </Notice>
      ) : !preview ? (
        <Spinner label="Checking who already holds this role" />
      ) : (
        <div className="space-y-4">
          <p className="text-[12.5px] leading-relaxed text-muted">
            {running === 0 ? (
              <>
                Nobody holds this role on an open case, so this change affects
                nothing that is already running.
              </>
            ) : (
              <>
                {pluralise(running, 'person holds', 'people hold')} this role
                on a case that is still open. Whether they get the new powers
                is your decision, and both answers are ordinary — a change
                made part-way through the year is often meant to apply only
                from that point on.
              </>
            )}
          </p>

          <div className="space-y-2.5">
            <Option
              selected={choice === false}
              onSelect={() => setChoice(false)}
              disabled={busy}
              icon={Lock}
              title="Only from now on"
              headline={
                running === 0
                  ? 'Nothing is running, so nothing changes.'
                  : `The ${pluralise(running, 'person', 'people')} who already hold it keep the powers they were given.`
              }
            >
              Nobody who already holds this role sees anything different. Each
              of them keeps the exact set of powers they were granted under,
              on every case they are on.
            </Option>

            <Option
              selected={choice === true}
              onSelect={() => setChoice(true)}
              disabled={busy}
              icon={Check}
              title="Everybody who holds it, on open cases"
              headline={
                blocked.length === 0
                  ? `All ${pluralise(moved, 'grant moves', 'grants move')} to version ${nextVersionNo}.`
                  : `${moved} of ${running} move to version ${nextVersionNo}. ${blocked.length} cannot.`
              }
            >
              {blocked.length === 0 ? (
                <>
                  Everybody holding it on an open case can take the new powers
                  without conflicting with another role they hold there.
                </>
              ) : (
                <>
                  A grant can only take the new powers if doing so would not
                  put that person on both sides of a separation on that case —
                  deciding and investigating, or holding a role that grants
                  roles alongside anything else. The ones that cannot keep the
                  powers they have until somebody changes their roles there.
                </>
              )}
            </Option>
          </div>

          {blocked.length > 0 && (
            <div className="rounded-md border border-line bg-surface-2 px-4 py-3">
              <div className="eyebrow mb-1.5">
                {pluralise(
                  blocked.length,
                  'grant that cannot move',
                  'grants that cannot move',
                )}
              </div>
              <p className="mb-2 text-[12px] leading-relaxed text-muted">
                Nothing is wrong with these. Each person keeps the powers they
                were given until their roles on that case change.
              </p>
              <ul className="space-y-1.5">
                {blocked.map((b) => (
                  <li key={`${b.reference}-${b.person}`} className="text-[12px]">
                    <span className="mono text-accent">{b.reference}</span>
                    <span className="text-muted">
                      {' '}
                      — {b.person || 'somebody'}:{' '}
                    </span>
                    <span className="text-faint">{b.reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="text-[11.5px] leading-relaxed text-faint">
            Grants on closed cases are never changed, by either answer
            {closed > 0
              ? ` — ${pluralise(closed, 'grant is', 'grants are')} on closed cases`
              : ''}
            . A closed case is the record of who had what access while it ran,
            so it keeps it permanently.
          </p>
        </div>
      )}

      <footer className="mt-5 flex flex-wrap items-center justify-end gap-2 border-t border-line pt-4">
        <Button variant="ghost" disabled={busy} onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          busy={busy}
          disabled={!preview || choice === null || busy}
          onClick={() => onConfirm(choice)}
        >
          {choice === null
            ? 'Choose an option'
            : choice
              ? 'Save and update existing holders'
              : 'Save for new grants only'}
        </Button>
      </footer>
    </Modal>
  )
}

function Option({
  selected,
  onSelect,
  disabled,
  icon: Icon,
  title,
  headline,
  children,
}) {
  return (
    <label
      className={`flex cursor-pointer items-start gap-3 rounded-md border px-4 py-3 transition ${
        selected
          ? 'border-accent-line bg-accent-dim'
          : 'border-line bg-surface-2 hover:border-line-strong'
      } ${disabled ? 'opacity-60' : ''}`}
    >
      <input
        type="radio"
        name="apply-role-to-open-cases"
        className="sr-only"
        checked={selected}
        disabled={disabled}
        onChange={onSelect}
      />
      <span
        className={`mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full border ${
          selected ? 'border-accent-line text-accent' : 'border-line text-faint'
        }`}
        aria-hidden="true"
      >
        <Icon size={13} strokeWidth={1.9} />
      </span>
      <span className="min-w-0">
        <span className="block font-head text-[13px] font-semibold text-ink">
          {title}
        </span>
        <span className="mt-0.5 block text-[12.5px] font-medium text-muted">
          {headline}
        </span>
        <span className="mt-1 block text-[12px] leading-relaxed text-faint">
          {children}
        </span>
      </span>
    </label>
  )
}
